import React, {
  useEffect,
  useState,
  forwardRef,
  useImperativeHandle,
  useMemo,
} from "react";
import { Form, Row, Card, Alert, Spinner } from "react-bootstrap";
import FormikSelectField from "../../../../shared/components/FormikSelectField";
import { Field, Formik, Form as FormikForm } from "formik";
import FormikCheckboxField from "../../../../shared/components/FormikCheckboxField";
import { useDispatch, useSelector } from "react-redux";
import { loadNotificationVariablesRequest } from "../redux/Actions";
import { loadActiveEmployeeDataRequest } from "../../../employees/redux/Actions";
import DefaultButton from "../../../../shared/components/DefaultButton";


const DEFAULT_EMAIL_BODY = `Ticket #{TicketNumber} - {Title}

 
Status: {Status}
Assigned To: {AssignedTo}
Request User: {RequestedBy}
Location: {RegionName} - {StoreName}

This ticket has been open for more than 30 minutes without assignment.

View Ticket: {TicketUrl} `;

// ─── Hardcoded HTML template to copy ─────────────────────────────────────────
const HARDCODED_HTML = `<!DOCTYPE html>
<html lang="en">
<body style="font-family: Arial, sans-serif; color: #333; line-height: 1.6; margin: 0; padding: 0;">
    <div style="max-width: 600px; margin: 0 auto; padding: 20px;">
        <div style="background-color: #1565C0; color: white; padding: 20px; text-align: center; border-radius: 4px 4px 0 0;">
            <h1 style="margin: 0; font-size: 22px;">New Reply on Your Ticket</h1>
        </div>
        <div style="padding: 24px; background-color: #f9f9f9; border: 1px solid #e0e0e0;">
            <p style="margin-top: 0;">The support team has replied to your ticket.</p>
            <table style="width: 100%; border-collapse: collapse; background: white; border-radius: 4px; overflow: hidden; margin-bottom: 16px;">
                <tr style="border-bottom: 1px solid #eee;">
                    <td style="padding: 10px 14px; font-weight: bold; width: 40%; color: #555;">Ticket #</td>
                    <td style="padding: 10px 14px;">\${ticketNumber}</td>
                </tr>
                <tr style="background-color: #fafafa;">
                    <td style="padding: 10px 14px; font-weight: bold; color: #555;">Subject</td>
                    <td style="padding: 10px 14px;">\${title}</td>
                </tr>
            </table>
            <div style="background: white; border-left: 4px solid #1565C0; padding: 14px 18px; border-radius: 0 4px 4px 0;">
                <p style="margin: 0 0 6px 0; font-size: 12px; color: #888;">Reply from <strong>\${senderName || 'Support Team'}</strong></p>
                <p style="margin: 0; white-space: pre-wrap;">\${messageBody}</p>
            </div>
        </div>
        <div style="text-align: center; padding: 14px; color: #888; font-size: 12px; border-top: 1px solid #e0e0e0;">
            <p style="margin: 0;">Wireless Vision LLC &bull; 205 Pierce St.Unit 99 &bull; Birmingham, MI 48009</p>
        </div>
    </div>
</body>
</html>`;

const NotificationSettings = forwardRef(({ isEdit, initialData }, ref) => {
  const formikRef = React.useRef();
  const { notificationVariables, loadingNotification } = useSelector(
    (state) => state.ticketingDropdowns
  );
  const dispatch = useDispatch();
  const { active_employees } = useSelector((store) => store.employee);

  useEffect(() => {
    dispatch(loadActiveEmployeeDataRequest());
  }, [dispatch]);

  const [subjectError, setSubjectError] = useState("");
  const [bodyError, setBodyError] = useState("");
  const [showPreview, setShowPreview] = useState(false);
  const [copied, setCopied] = useState(false);
  const parsePgArray = (val) => {
                    if (Array.isArray(val)) return val;
                    if (typeof val === "string" && val.startsWith("{") && val.endsWith("}")) {
                      return val
                        .slice(1, -1)
                        .split(",")
                        .map((s) => s.trim().replace(/^"|"$/g, ""))
                        .filter(Boolean);
                    }
                    return val ? [val] : [];
                  };

  const initialValues = useMemo(() => {
    if (isEdit && initialData) {
      return {
        notify_cc_recipients: false,
        notify_custom_recipient: parsePgArray(initialData.notify_custom_recipient_id).length > 0,
        notify_custom_recipient_id: parsePgArray(initialData.notify_custom_recipient_id),  
        notify_assigned_admin_group: initialData.notify_assigned_admin_group ?? false,
        notify_request_user: initialData.notify_request_user ?? false,
        notify_assigned_admin: initialData.notify_assigned_admin ?? false,
        method: initialData.notification_method ?? "email",
        subject: initialData.notification_subject ?? "",
        body: initialData.notification_body ?? "",
      };
    }
    return {
      notify_cc_recipients: false,
      notify_custom_recipient: false,
      notify_custom_recipient_id: [],
      notify_assigned_admin_group: false,
      notify_request_user: true,
      notify_assigned_admin: false,
      method: "email",
      subject: "Urgent: Ticket #{TicketNumber} requires attention",
      body: DEFAULT_EMAIL_BODY,
    };
  }, [isEdit, initialData]);

  const [currentMethod, setCurrentMethod] = useState(initialValues.method);

  const normalizeVar = (v) =>
    String(v)
      .replace(/^\s*\{?/, "")
      .replace(/\}?\s*$/, "")
      .trim();

  const allowedVarSet = useMemo(
    () => new Set(notificationVariables.map(normalizeVar)),
    [notificationVariables]
  );

  useEffect(() => {
    if (!formikRef.current) return;
    if (currentMethod === "email" || currentMethod === "both") {
      const formik = formikRef.current;
      if (!formik.values.body || formik.values.body.trim() === "") {
        if (isEdit && initialData?.notification_body) {
          formik.setFieldValue("body", initialData.notification_body);
        } else {
          formik.setFieldValue("body", DEFAULT_EMAIL_BODY);
        }
      }
    }
  }, [currentMethod, isEdit, initialData]);

  const validateTemplate = (text, allowed) => {
    const errors = [];
    const strippedText = text.replace(/\$\{[^}]*\}/g, "");
    const opens = (strippedText.match(/\{/g) || []).length;
    const closes = (strippedText.match(/\}/g) || []).length;
    if (opens !== closes) {
      errors.push(
        'Your template has unbalanced braces. Make sure every "{" has a matching "}".'
      );
    }
    const tokenRe = /(?<!\$)\{([^{}]+)\}/g;
    let m;
    while ((m = tokenRe.exec(text)) !== null) {
      const inner = m[1] || "";
      const raw = inner;
      const trimmed = raw.trim();
      if (!trimmed) continue;
      const hasOuterSpaces = raw !== trimmed;
      const cleanedNoSpaces = trimmed.replace(/\s+/g, "");
      const hasInnerSpaces = cleanedNoSpaces !== trimmed;
      if (hasOuterSpaces || hasInnerSpaces) {
        errors.push(
          `Remove spaces inside braces: "{${raw}}" → "{${cleanedNoSpaces}}".`
        );
      }
      if (!(hasOuterSpaces || hasInnerSpaces) && !allowed.has(cleanedNoSpaces)) {
        errors.push(`Unknown variable: "{${raw}}".`);
      }
    }
    return Array.from(new Set(errors));
  };

  const toBullets = (errs) => errs.map((e) => `• ${e}`).join("\n");

  const handleSubjectChange = (val, setFieldValue) => {
    setFieldValue("subject", val);
    const errs = validateTemplate(val, allowedVarSet);
    setSubjectError(toBullets(errs));
  };

  const handleBodyChange = (val, setFieldValue) => {
    setFieldValue("body", val);
    const errs = validateTemplate(val, allowedVarSet);
    setBodyError(toBullets(errs));
  };

  // ─── Copy HTML to clipboard, body unchanged ───────────────────────────────
  const handleCopyHtml = () => {
    navigator.clipboard.writeText(HARDCODED_HTML).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  // ─── Preview: if body has HTML render it, else wrap plain text ───────────
  const getPreviewHtml = (body) => {
    if (!body) return "<p style='color:#888;padding:20px;'>Nothing to preview yet.</p>";
    const isHtml = /<!DOCTYPE|<html/i.test(body);
    if (isHtml) return body;
    // Plain text — wrap nicely
    return `<!DOCTYPE html>
<html lang="en">
<body style="font-family: Arial, sans-serif; color: #333; line-height: 1.6; margin: 0; padding: 20px;">
  <div style="max-width: 600px; margin: 0 auto; white-space: pre-wrap;">${body}</div>
</body>
</html>`;
  };

  useEffect(() => {
    if (!notificationVariables.length && !loadingNotification) {
      dispatch(loadNotificationVariablesRequest());
    }
  }, []);

  useEffect(() => {}, [notificationVariables]);

  useEffect(() => {
    if (formikRef.current) {
      const { values } = formikRef.current;
      const subjErrs = validateTemplate(values.subject, allowedVarSet);
      const bodyErrs = validateTemplate(values.body, allowedVarSet);
      setSubjectError(toBullets(subjErrs));
      setBodyError(toBullets(bodyErrs));
    }
  }, [allowedVarSet]);

  useImperativeHandle(ref, () => ({
    validateAndGetData: () => {
      const formik = formikRef.current;
      if (!formik) return { isValid: false, data: null };
      const { values } = formik;
      if (!values.subject.trim()) {
        setSubjectError("• Notification subject cannot be empty.");
        return { isValid: false, data: null };
      }
      if (values.method === "email" || values.method === "both") {
        if (!values.body.trim()) {
          setBodyError("• Notification body cannot be empty.");
          return { isValid: false, data: null };
        }
      }
      const subjErrs = validateTemplate(values.subject, allowedVarSet);
      const bodyErrs =
        values.method === "email" || values.method === "both"
          ? validateTemplate(values.body, allowedVarSet)
          : [];
      setSubjectError(toBullets(subjErrs));
      setBodyError(toBullets(bodyErrs));
      if (subjErrs.length || bodyErrs.length) {
        return { isValid: false, data: null };
      }
      const data = {
        notify_assigned_admin: values.notify_assigned_admin,
        notify_assigned_admin_group: values.notify_assigned_admin_group,
        notify_cc_recipients: false,
        notify_custom_recipient: Array.isArray(values.notify_custom_recipient_id) && values.notify_custom_recipient_id.length > 0,
        notify_custom_recipient_id:  Array.isArray(values.notify_custom_recipient_id) &&  values.notify_custom_recipient_id.length ? values.notify_custom_recipient_id.map(Number) : null,
        notify_request_user: values.notify_request_user,
        method: values.method,
        subject: values.subject,
        body:
          values.method === "email" || values.method === "both"
            ? values.body
            : null,
      };
      return { isValid: true, data };
    },
  }));

  return (
    <Formik innerRef={formikRef} initialValues={initialValues} enableReinitialize>
      {({ values, setFieldValue }) => {
        if (values.method !== currentMethod) {
          setCurrentMethod(values.method);
        }
        const hasCustomRecipients = Array.isArray(values.notify_custom_recipient_id) && values.notify_custom_recipient_id.length > 0;
          if (values.notify_custom_recipient !== hasCustomRecipients) {
            setFieldValue("notify_custom_recipient", hasCustomRecipients);
          }
        return (
          <FormikForm>
            <Card
              className="border-0 shadow-sm rounded-3 p-4"
              style={{ background: "#fff" }}
            >
              <Card.Header>Notification Settings</Card.Header>
              <Card.Body>
                {/* Notify checkboxes */}
                <div className="mb-3">
                  <Form.Label>
                    <b>Notify</b>
                  </Form.Label>
                  <div>
                    <FormikCheckboxField
                      name="notify_assigned_admin"
                      label="Notify assigned administrator"
                      value={values.notify_assigned_admin}
                      setFieldValue={setFieldValue}
                      style={{ marginRight: "0.3rem", marginBottom: "0.7rem" }}
                    />
                    <FormikCheckboxField
                      name="notify_request_user"
                      label="Notify the service record's Request User"
                      value={values.notify_request_user}
                      setFieldValue={setFieldValue}
                      style={{ marginRight: "0.3rem", marginBottom: "0.7rem" }}
                    />
                    <FormikCheckboxField
                      name="notify_assigned_admin_group"
                      label="Notify the assigned admin group"
                      value={values.notify_assigned_admin_group}
                      setFieldValue={setFieldValue}
                      style={{ marginRight: "0.3rem", marginBottom: "0.7rem" }}
                    />
                  </div>
                </div>

                {/* Custom Employee */}
                <Row className="my-3">
                  <FormikSelectField
                    name="notify_custom_recipient_id"
                    label="Custom Employee To Notify"
                    value={values.notify_custom_recipient_id}
                    onChange={setFieldValue}
                    isMulti
                  >
                    {/* <option value="">Select employee</option> */}
                    {active_employees?.map((emp) => (
                      <option key={emp.id} value={emp.id}>
                        {emp.first_name} {emp.last_name} , {emp.employee_id}
                      </option>
                    ))}
                  </FormikSelectField>
                </Row>

                {/* Notification Method */}
                <Row className="my-3">
                  <FormikSelectField
                    name="method"
                    label="Notification Method"
                    value={values.method}
                    onChange={setFieldValue}
                    required
                  >
                    <option value="email">Email</option>
                    <option value="push">Push</option>
                    <option value="both">Both</option>
                  </FormikSelectField>
                </Row>

                {/* Subject */}
                <Row className="my-3">
                  <div className="col-md-12">
                    <Form.Label htmlFor="subject">
                      Notification Subject <span className="text-danger">*</span>
                    </Form.Label>
                    <Field
                      className={`form-control ${subjectError ? "is-invalid" : ""}`}
                      type="text"
                      name="subject"
                      id="subject"
                      placeholder="Enter notification subject"
                      onChange={(e) =>
                        handleSubjectChange(e.target.value, setFieldValue)
                      }
                    />
                    {subjectError && (
                      <div
                        className="text-danger mt-1"
                        style={{ whiteSpace: "pre-wrap", fontSize: "0.875rem" }}
                      >
                        {subjectError}
                      </div>
                    )}
                  </div>
                </Row>

                {/* Body */}
                {(values.method === "email" || values.method === "both") && (
                  <Row className="my-3">
                    <div className="col-md-12">
                      <div className="d-flex justify-content-between align-items-center mb-1">
                        <Form.Label htmlFor="body" className="mb-0">
                          Notification Body <span className="text-danger">*</span>
                        </Form.Label>
                        <div className="d-flex gap-2">
                          {/* Copy HTML button — shows "Copied!" feedback */}
                          <button
                            type="button"
                            onClick={handleCopyHtml}
                            title={copied ? "Copied!" : "Copy HTML Template"}
                            style={{
                              background: "none",
                              border: "none",
                              cursor: "pointer",
                              fontSize: "18px",
                              color: copied ? "#28a745" : "#1565C0",
                              padding: "4px 8px",
                              transition: "color 0.2s",
                            }}
                          >
                            <i className={copied ? "fas fa-check" : "fas fa-copy"} />
                          </button>
                          <DefaultButton
                            buttonClass="default-btn-color btn-sm"
                            buttonTitle="Preview"
                            actionBlock={() => setShowPreview(true)}
                          />
                        </div>
                      </div>

                      <Field
                        as="textarea"
                        className={`form-control ${bodyError ? "is-invalid" : ""}`}
                        rows={7}
                        style={{
                          minHeight: "200px",
                          fontFamily: "monospace",
                          fontSize: "13px",
                        }}
                        name="body"
                        id="body"
                        placeholder="Enter notification body — paste HTML or plain text"
                        onChange={(e) =>
                          handleBodyChange(e.target.value, setFieldValue)
                        }
                      />
                      {bodyError && (
                        <div
                          className="text-danger mt-1"
                          style={{ whiteSpace: "pre-wrap", fontSize: "0.875rem" }}
                        >
                          {bodyError}
                        </div>
                      )}
                    </div>
                  </Row>
                )}

                {/* Available Variables */}
                <Alert
                  variant="success"
                  className="my-4"
                  style={{ fontSize: "0.95em" }}
                >
                  {loadingNotification ? (
                    <>
                      <Spinner size="sm" /> Loading variables...
                    </>
                  ) : (
                    <>
                      <b>Available Variables:</b>{" "}
                      {notificationVariables.length > 0
                        ? notificationVariables.join(", ")
                        : "No variables available."}
                    </>
                  )}
                </Alert>
              </Card.Body>
            </Card>

            {/* ─── Preview Modal ─────────────────────────────────────────────── */}
            {showPreview && (
              <div
                style={{
                  position: "fixed",
                  top: 0,
                  left: 0,
                  width: "100vw",
                  height: "100vh",
                  background: "rgba(0,0,0,0.55)",
                  zIndex: 9999,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
                onClick={() => setShowPreview(false)}
              >
                <div
                  style={{
                    background: "#fff",
                    borderRadius: "8px",
                    width: "720px",
                    maxHeight: "88vh",
                    display: "flex",
                    flexDirection: "column",
                    boxShadow: "0 8px 32px rgba(0,0,0,0.25)",
                  }}
                  onClick={(e) => e.stopPropagation()}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      padding: "14px 20px",
                      borderBottom: "1px solid #e0e0e0",
                    }}
                  >
                    <span style={{ fontWeight: 600, fontSize: "16px" }}>
                      Email Preview
                    </span>
                    <DefaultButton
                      buttonClass="default-btn-color"
                      buttonTitle="Close"
                      actionBlock={() => setShowPreview(false)}
                    />
                  </div>
                  <div style={{ flex: 1, overflow: "hidden", padding: "16px" }}>
                    <iframe
                      srcDoc={getPreviewHtml(values.body)}
                      style={{
                        width: "100%",
                        height: "100%",
                        minHeight: "500px",
                        border: "1px solid #ddd",
                        borderRadius: "4px",
                        background: "#fff",
                      }}
                      title="Email Preview"
                      sandbox="allow-same-origin"
                    />
                  </div>
                </div>
              </div>
            )}
          </FormikForm>
        );
      }}
    </Formik>
  );
});

export default NotificationSettings;